import { FC, useState, useEffect } from "react"
import { AttachmentModel } from "./components/AttachmentModel"
import { Attachment3D } from "./components/Attachment3D"
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
} from "@mui/material"
import { AttachmentList } from "./AttachmentList"
import { AttachmentSettingTabs } from "./components/AttachmentSettingTabs"
import { useAppSelector, useAppDispatch } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import { attachmentModule, wasmModule } from "@/gluelayer"
import {
  updateClinicalSettings,
  fetchClinicalSettings,
} from "@/core/app/slices/user/userApis"
import { ULoading } from "@/ui-component"

interface GroupProtocolProps {
  open: boolean
  onClose: () => void
}

export const GroupProtocol: FC<GroupProtocolProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch()
  //当前tab
  const [activeTabId, setActiveTabId] = useState<number>(1)
  //正在编辑tab
  const [editTabId, setEditId] = useState<number>(0)
  //添加 protocol dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [protocolName, setProtocolName] = useState<string>("")
  //确认保存 dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const [wasmReady, setWasmReady] = useState(false)

  useEffect(() => {
    if (!open) {
      setWasmReady(false)
    }
  }, [open])

  const DefinAttachmentTemplate = useAppSelector(
    (state: RootState) =>
      state.userService.clinicalSettings.DefinAttachmentTemplate,
  )
  // 传递setting数据给wasm和初始化显示设置
  const passDatatoWasm = () => {
    try {
      // const windowTitle = document.title
      // wasmModule.module._attachmentSettingPreview(
      //   wasmModule.module.allocateUTF8(windowTitle),
      // )

      attachmentModule.SetGroupAttachmentSettingJson(
        JSON.stringify(DefinAttachmentTemplate),
      )
      console.log("DefinAttachmentTemplate:", DefinAttachmentTemplate)
      // 改变附件回调
      attachmentModule.SetOnGroupAttachmentChangedCB((id) => {
        setEditId(id)
      })
      wasmModule.ulabwinIns.ShowGroupAttachmentSetting(activeTabId)
    } catch (err) {
      console.error("Pass data to wasm failed:", err)
      setWasmReady(true)
    }

    setWasmReady(true)
  }
  // 检查是否有编辑，如果有则弹出确认对话框
  const checkPendingChanges = (action: () => void) => {
    if (editTabId !== 0) {
      setPendingAction(() => action)
      setConfirmDialogOpen(true)
    } else {
      action()
    }
  }
  // change tab
  const onTabChange = (tab) => {
    checkPendingChanges(() => {
      setActiveTabId(tab.id)
      wasmModule.ulabwinIns.ShowGroupAttachmentSetting(tab.id)
    })
  }
  // delete
  const onDeleteClick = (tab) => {
    attachmentModule.DelGroupAttachment(tab.id)
    const json = JSON.parse(attachmentModule.GetGroupAttachmentSettingJson())
    saveToService(json, () => {
      setEditId(0)
      setActiveTabId(1)
      wasmModule.ulabwinIns.ShowGroupAttachmentSetting(1)
    })
  }
  // save
  const onSaveClick = (tab) => {
    const json = JSON.parse(attachmentModule.GetGroupAttachmentSettingJson())
    saveToService(json, () => {
      setEditId(0)
    })
  }
  // save to service
  const saveToService = (json: any, callback?: () => void) => {
    dispatch(
      updateClinicalSettings({
        DefinAttachmentTemplate: json,
      }),
    ).then((res) => {
      if (res && res.payload && res.payload.response_code === 200) {
        dispatch(fetchClinicalSettings())
        callback?.()
      } else {
        console.error("Save failed")
      }
    })
  }
  // add protocol
  const onAddClick = () => {
    checkPendingChanges(() => {
      setAddDialogOpen(true)
      setProtocolName("")
    })
  }
  // handle add dialog ok
  const handleAddDialogOk = () => {
    const trimmedName = protocolName.trim()
    const isDuplicate = DefinAttachmentTemplate.some(tab => tab.name.trim() === trimmedName)

    if (isDuplicate) {
      setErrorDialogOpen(true)
      return
    }

    const maxId = DefinAttachmentTemplate.reduce(
      (max, tab) => Math.max(max, tab.id),
      0,
    )
    const newId = maxId + 1
    attachmentModule.AddNewGroupAttachment(newId, protocolName)
    const json = JSON.parse(attachmentModule.GetGroupAttachmentSettingJson())
    saveToService(json, () => {
      setAddDialogOpen(false)
      setActiveTabId(newId)
      wasmModule.ulabwinIns.ShowGroupAttachmentSetting(newId)
    })
  }
  // handle add dialog cancel
  const handleAddDialogCancel = () => {
    setAddDialogOpen(false)
  }
  // 确认对话框 - 取消
  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false)
    const actionToExecute = pendingAction
    setPendingAction(null)
    // 不保存，重新传递数据
    attachmentModule.SetGroupAttachmentSettingJson(
      JSON.stringify(DefinAttachmentTemplate),
    )
    setEditId(0)
    // 执行最初的功能
    actionToExecute?.()
  }
  // 确认对话框 - OK
  const handleConfirmOk = () => {
    setConfirmDialogOpen(false)
    const actionToExecute = pendingAction
    const json = JSON.parse(attachmentModule.GetGroupAttachmentSettingJson())
    saveToService(json, () => {
      setPendingAction(null)
      setEditId(0)
      // 执行最初的功能
      actionToExecute?.()
    })
  }
  // 包装 onClose
  const handleModelClose = () => {
    checkPendingChanges(() => {
      onClose()
    })
  }
  return (
    <AttachmentModel
      title="Group Protocol"
      open={open}
      onClose={handleModelClose}
    >
      {activeTabId > 4 && (
        <Box
          sx={{
            position: "absolute",
            top: 102,
            left: 16,
            width: "320px",
            height: "auto",
            zIndex: 1,
          }}
        >
          <AttachmentList />
        </Box>
      )}
      <Box
        sx={{
          position: "absolute",
          top: 50,
          left: 0,
          width: "100%",
          height: "50px",
          zIndex: 1,
        }}
      >
        <AttachmentSettingTabs
          tabs={DefinAttachmentTemplate}
          activeTabId={activeTabId}
          onActiveTabChange={setActiveTabId}
          onTabChange={onTabChange}
          onSaveClick={onSaveClick}
          onAddClick={onAddClick}
          onDeleteClick={onDeleteClick}
          isDataModified={(tabId) => tabId === editTabId}
        />
      </Box>
      <Attachment3D initCallBack={passDatatoWasm} />
      <Dialog
        open={addDialogOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleAddDialogCancel()
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            pb: 1,
          }}
        >
          Add Protocol
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Protocol Name"
            type="text"
            fullWidth
            variant="outlined"
            value={protocolName}
            onChange={(e) => {
              const value = e.target.value
              if (value.length <= 20) {
                setProtocolName(value)
              }
            }}
            helperText={`${protocolName.length}/20`}
            sx={{
              fontSize: "16px",
              "& .MuiInputLabel-root": {
                fontSize: "14px",
              },
              "& .MuiInputBase-input": {
                fontSize: "16px",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleAddDialogCancel}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddDialogOk}
            variant="contained"
            disabled={!protocolName.trim()}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={errorDialogOpen}
        maxWidth="xs"
      >
        <DialogContent >
          This template is exist, please change it.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmDialogOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleConfirmCancel()
          }
        }}
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            pb: 1,
          }}
        >
          Confirm
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          Do you want to save these changes?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleConfirmCancel}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmOk} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <ULoading isLoading={!wasmReady} />
    </AttachmentModel>
  )
}
